import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { ConfettiTypes, useConfetti } from "../../hooks/useConfetti";

// Room kept between the letter's words and the pocket's sloping edges
const CLEARANCE_X = 14;
const CLEARANCE_Y = 6;

/** The on-screen box of every line of text inside `node` */
const textLineRects = (node) => {
  const rects = [];
  const range = document.createRange();
  const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
  for (let text = walker.nextNode(); text; text = walker.nextNode()) {
    if (!text.textContent.trim()) continue;
    range.selectNodeContents(text);
    for (const r of range.getClientRects()) {
      if (r.width && r.height) rects.push(r);
    }
  }
  return rects;
};

/**
 * How much further the letter has to rise out of the envelope so that every line on it
 * (the heading, the message and any "invited by" line) is clear of the pocket. The
 * pocket's opening is a V: at depth y below the envelope's top it is W * (1 - y / (H / 2))
 * wide. Measured within the letter, so the letter's current slide (or transition) doesn't
 * matter.
 */
const liftToClear = (envelope, letter, content) => {
  const W = envelope.offsetWidth;
  const H = envelope.offsetHeight;
  if (!W || !H || !letter.offsetWidth) return 0;
  const letterBox = letter.getBoundingClientRect();
  // Screen px per CSS px, in case anything around the envelope is scaled
  const scale = letterBox.width / letter.offsetWidth || 1;
  const centre = letterBox.left + letterBox.width / 2; // the letter is centred in the envelope
  // Where the letter's top sits below the envelope's top once it has risen (styles: --letter-rise)
  const rise = parseFloat(getComputedStyle(letter).getPropertyValue("--letter-rise")) || 170;
  const shownTop = letter.offsetTop - rise;

  let lift = 0;
  for (const r of textLineRects(content)) {
    const depth = shownTop + (r.bottom - letterBox.top) / scale + CLEARANCE_Y;
    const halfWidth = Math.max(centre - r.left, r.right - centre) / scale + CLEARANCE_X;
    const allowed = (H / 2) * (1 - halfWidth / (W / 2));
    lift = Math.max(lift, depth - allowed);
  }
  return Math.ceil(lift);
};

/**
 * How much taller the letter has to be to hold all its content (a long "invited by" can
 * run past its fixed height). Measured against the letter's height without the extra
 * height it is given for --letter-grow and --letter-lift.
 */
const growToFit = (letter, content) => {
  const style = getComputedStyle(letter);
  const extra =
    (parseFloat(style.getPropertyValue("--letter-grow")) || 0) +
    (parseFloat(style.getPropertyValue("--letter-lift")) || 0);
  return Math.max(0, Math.ceil(content.offsetHeight - (letter.clientHeight - extra)));
};

const NO_FIT = { lift: 0, grow: 0 };

export default function LetterEnvelope({ confettiType = ConfettiTypes.default }) {
  const wrapperRef = useRef(null);
  const envelopeRef = useRef(null);
  const letterRef = useRef(null);
  const contentRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [fit, setFit] = useState(NO_FIT);
  const { showConfetti } = useConfetti();
  // Who the invitation is from (the invite's `invitedBy`), shown under the letter's message
  const invitedBy = useSelector((state) => state.inviteDetails?.invite?.invitedBy);
  const inviter = typeof invitedBy === "string" ? invitedBy.trim() : "";

  // The letter grows to hold all its words if needed, and rises just far enough for every
  // line to clear the pocket. That depends on the theme's fonts, the screen size and the
  // "invited by" line, so it is measured rather than fixed.
  useEffect(() => {
    const envelope = envelopeRef.current;
    const letter = letterRef.current;
    const content = contentRef.current;
    if (!envelope || !letter || !content) return undefined;
    let active = true;
    const measure = () => {
      if (!active) return;
      const next = { lift: liftToClear(envelope, letter, content), grow: growToFit(letter, content) };
      setFit((prev) => (prev.lift === next.lift && prev.grow === next.grow ? prev : next));
    };
    measure();
    // Fonts arriving and the screen size changing both move the lines
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    observer?.observe(content);
    observer?.observe(envelope);
    window.addEventListener("resize", measure);
    document.fonts?.ready.then(measure);
    return () => {
      active = false;
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [inviter]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true); // 👈 trigger animation
          showConfetti(confettiType);
        } else {
          setIsVisible(false); // optional: reverse on scroll up
        }
      },
      {
        threshold: 0.6, // 👈 60% visible triggers animation
      },
    );

    if (wrapperRef.current) {
      observer.observe(wrapperRef.current);
    }

    return () => {
      if (wrapperRef.current) {
        observer.unobserve(wrapperRef.current);
      }
    };
  }, []);

  return (
    <section
      className="wpo-contact-section-s6 section-padding section"
      style={{ padding: "60px 0 140px 0" }}
    >
      <div className="letterEnvelope-page">
        <div /> {/* spacer */}
        <div ref={wrapperRef} className="letterEnvelope-wrapper">
          <div
            ref={envelopeRef}
            className={`letterEnvelope ${isVisible ? "show" : ""}`}
            // Read by the letter (styles: page/_letterEnvelope.scss), and the room the envelope
            // keeps above it for the letter's extra rise
            style={
              fit.lift || fit.grow
                ? { "--letter-lift": `${fit.lift}px`, "--letter-grow": `${fit.grow}px` }
                : undefined
            }
          >
            <div className="letterEnvelope-front letterEnvelope-flap"></div>
            <div className="letterEnvelope-front letterEnvelope-pocket"></div>

            <div ref={letterRef} className="letterEnvelope-letter">
              <div ref={contentRef} className="letterEnvelope-content">
                <h2 className="orangeText">You're Invited 💌</h2>
                <p className="letter-text">
                  Together with our families, We invite you to celebrate the
                  beginning of our forever. <br />
                  <br />
                  Your presence will make our day even more special😊.
                </p>
                {inviter && (
                  <p className="letter-invited-by">
                    {inviter}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
